import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { forkJoin } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { PermissionService } from '../../security/permission.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { AuditEvidence, AuditSourceAdapter, SelectOption } from '../audit.models';
import { AuditService } from '../audit.service';
import { auditNavItems, formatAuditDate, statusSeverity } from '../audit-ui';

@Component({
  selector: 'app-audit-evidence-locker',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    IftaLabelModule,
    InputGroupAddonModule,
    InputGroupModule,
    InputTextModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    TagModule,
    TooltipModule,
    PageShellComponent,
    PageHeroComponent,
    PageDataPanelComponent
  ],
  template: `
    @if (loading) {
      <p-progressBar mode="indeterminate"></p-progressBar>
    }

    <app-page-shell>
      <app-page-hero eyebrow="Audit Evidence" title="Evidence Locker" subtitle="Search source-linked evidence metadata, system snapshots, hashes, and imported source records.">
        <div nav>
          @for (item of visibleNavItems; track item.route) {
            <a pButton [routerLink]="item.route" [label]="item.label" [icon]="item.icon" [outlined]="true" severity="secondary"></a>
          }
        </div>
        <div actions>
          <a pButton routerLink="/audit/cases/create" label="New Case" icon="pi pi-plus" [outlined]="true" severity="secondary"></a>
        </div>
      </app-page-hero>

      <app-page-data-panel title="Evidence Search" subtitle="Filter by source module, source resource type, resource number, file metadata, or SHA-256 hash." [resultText]="filteredEvidence.length + ' records'">
        <div class="filter-grid">
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-th-large"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select inputId="evidence-source-module" [options]="sourceModuleOptions" [(ngModel)]="sourceModule" optionLabel="label" optionValue="value" [showClear]="true"></p-select>
              <label for="evidence-source-module">Source module</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-box"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select inputId="evidence-resource-type" [options]="sourceResourceTypeOptions" [(ngModel)]="sourceResourceType" optionLabel="label" optionValue="value" [showClear]="true"></p-select>
              <label for="evidence-resource-type">Resource type</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-search"></i></p-inputgroup-addon>
            <p-iftalabel>
              <input pInputText id="evidence-search" [(ngModel)]="searchText">
              <label for="evidence-search">Search</label>
            </p-iftalabel>
          </p-inputgroup>
        </div>

        <p-table [value]="filteredEvidence" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '980px' }">
          <ng-template pTemplate="header">
            <tr>
              <th>Evidence</th>
              <th>Source</th>
              <th>Visibility</th>
              <th>Captured</th>
              <th>Case</th>
              <th>Hash</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-item>
            <tr>
              <td>
                <div class="audit-stack">
                  <strong>{{ item.fileName || item.evidenceType || 'Evidence metadata' }}</strong>
                  <small>{{ item.fileUrl || metadataSummary(item) }}</small>
                </div>
              </td>
              <td>
                <div class="audit-stack">
                  <strong>{{ item.sourceDisplayName || item.sourceResourceNumber || '-' }}</strong>
                  <small>{{ item.sourceModule || '-' }}/{{ item.sourceResourceType || '-' }}</small>
                </div>
              </td>
              <td><p-tag [value]="item.visibility || 'INTERNAL'" [severity]="statusSeverity(item.visibility)"></p-tag></td>
              <td>{{ formatDate(item.capturedAt) }}</td>
              <td>
                @if (item.auditCaseId) {
                  <a [routerLink]="['/audit/cases', item.auditCaseId]">Case #{{ item.auditCaseId }}</a>
                } @else {
                  -
                }
              </td>
              <td class="hash-cell" [pTooltip]="item.sha256Hash || ''">{{ item.sha256Hash || '-' }}</td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="6" class="text-center p-4">No evidence metadata found.</td></tr>
          </ng-template>
        </p-table>
      </app-page-data-panel>
    </app-page-shell>
  `,
  styles: [`
    .filter-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(180px, 1fr));
      gap: .85rem;
      margin-bottom: 1rem;
    }

    label {
      color: #334155;
      display: grid;
      gap: .35rem;
      min-width: 0;
    }

    label span {
      color: #64748b;
      font-size: .82rem;
      font-weight: 700;
    }

    input,
    p-iftalabel,
    p-inputgroup,
    p-select {
      width: 100%;
    }

    .audit-stack {
      display: flex;
      flex-direction: column;
      gap: .2rem;
      min-width: 0;
    }

    .audit-stack small {
      color: #64748b;
      font-size: .78rem;
      max-width: 420px;
      overflow-wrap: anywhere;
    }

    .hash-cell {
      font-family: "SFMono-Regular", Consolas, "Liberation Mono", monospace;
      max-width: 220px;
      overflow-wrap: anywhere;
    }

    @media (max-width: 900px) {
      .filter-grid {
        grid-template-columns: 1fr;
      }
    }
  `]
})
export class AuditEvidenceLockerComponent implements OnInit {
  private readonly auditService = inject(AuditService);
  private readonly permissionService = inject(PermissionService);

  loading = false;
  evidence: AuditEvidence[] = [];
  sourceAdapters: AuditSourceAdapter[] = [];
  sourceModule?: string;
  sourceResourceType?: string;
  searchText = '';

  readonly visibleNavItems = auditNavItems.filter(item => this.permissionService.hasPermission(item.permission));
  readonly formatDate = formatAuditDate;
  readonly statusSeverity = statusSeverity;

  ngOnInit(): void {
    this.loading = true;
    forkJoin({
      evidence: this.auditService.evidence(),
      sourceAdapters: this.auditService.sourceAdapters()
    }).subscribe({
      next: response => {
        this.evidence = response.evidence.data ?? [];
        this.sourceAdapters = response.sourceAdapters.data ?? [];
      },
      complete: () => this.loading = false
    });
  }

  get sourceModuleOptions(): SelectOption<string>[] {
    const modules = new Map<string, string>();
    for (const adapter of this.sourceAdapters) {
      if (adapter.sourceModule) {
        modules.set(adapter.sourceModule, adapter.sourceModule.replaceAll('_', ' '));
      }
    }
    return Array.from(modules.entries()).map(([value, label]) => ({ value, label }));
  }

  get sourceResourceTypeOptions(): SelectOption<string>[] {
    return this.sourceAdapters
      .filter(adapter => !this.sourceModule || adapter.sourceModule === this.sourceModule)
      .filter(adapter => !!adapter.sourceResourceType)
      .map(adapter => ({
        value: adapter.sourceResourceType!,
        label: `${adapter.displayName || adapter.sourceResourceType} (${adapter.sourceResourceType})`
      }));
  }

  get filteredEvidence(): AuditEvidence[] {
    const search = this.searchText.trim().toLowerCase();
    return this.evidence.filter(item => {
      const matchesModule = !this.sourceModule || item.sourceModule === this.sourceModule;
      const matchesType = !this.sourceResourceType || item.sourceResourceType === this.sourceResourceType;
      const haystack = [
        item.evidenceType,
        item.sourceModule,
        item.sourceResourceType,
        item.sourceResourceNumber,
        item.sourceDisplayName,
        item.fileName,
        item.fileUrl,
        item.sha256Hash,
        item.metadataJson
      ].join(' ').toLowerCase();
      return matchesModule && matchesType && (!search || haystack.includes(search));
    });
  }

  metadataSummary(item: AuditEvidence): string {
    if (!item.metadataJson) {
      return item.sourceResourceNumber || '-';
    }
    return item.metadataJson.length > 110 ? `${item.metadataJson.slice(0, 110)}...` : item.metadataJson;
  }
}
